import { useMemo, useState } from 'react';
import { Sparkles, RefreshCw } from 'lucide-react';
import { ErrorState } from '@/components/ui/States';
import { Select, Field } from '@/components/ui/Form';
import { PeriodTabs } from '@/components/dashboard/DashboardCards';
import { BusinessSnapshot } from '@/components/dashboard/BusinessSnapshot';
import { OrderFlow } from '@/components/dashboard/OrderFlow';
import { NeedsAttention } from '@/components/dashboard/NeedsAttention';
import { RevenuePerformance } from '@/components/dashboard/RevenuePerformance';
import { UserDistribution } from '@/components/dashboard/UserStatisticsCard';
import { SalesDetailsModal } from '@/components/dashboard/SalesDetailsModal';
import {
  useDashboardOrderStatistics,
  useDashboardSales,
  useDashboardZones,
} from '@/hooks/useDashboardQueries';
import { useDashboardRankings } from '@/hooks/useRankings';
import { RankingCard, rankName, type RankingWidgetConfig } from '@/components/dashboard/RankingCard';
import { DashboardFooter } from '@/components/dashboard/DashboardFooter';
import { EntityDetailModal, type EntityModalRef } from '@/components/dashboard/EntityDetailModal';
import { routes } from '@/routes/paths';
import type {
  CommissionPeriod,
  DashboardPeriod,
  UserOverviewPeriod,
} from '@/types/dashboard';
import type { RankingRowBase } from '@/types/rankings';

const PERIOD_OPTIONS: Array<{ id: DashboardPeriod; label: string }> = [
  { id: 'this_year', label: 'This Year' },
  { id: 'this_month', label: 'This Month' },
  { id: 'this_week', label: 'This Week' },
];

function normalizeSeries(v: unknown): number[] {
  if (Array.isArray(v)) return v.map((n) => Number(n) || 0);
  if (v && typeof v === 'object') {
    return Object.keys(v as Record<string, number>)
      .sort((a, b) => Number(a) - Number(b))
      .map((k) => Number((v as Record<string, number>)[k]) || 0);
  }
  return [];
}

function normalizeLabels(v: unknown): string[] {
  if (Array.isArray(v)) return v.map((s) => String(s));
  return ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
}

function numOrDash(v: unknown): string {
  const n = Number(v);
  return Number.isFinite(n) ? String(n) : '—';
}

function sumSeries(values: number[]): number | null {
  return values.length ? values.reduce((a, b) => a + b, 0) : null;
}

const RANK_WIDGETS: Record<string, RankingWidgetConfig> = {
  topStores: {
    title: 'Top Selling Stores',
    subtitle: 'Ranked by order count',
    emptyTitle: 'No stores available',
    emptyDescription: 'No stores have orders for this selection yet.',
    viewAllTo: routes.stores,
    entityType: 'restaurant' as const,
    primaryName: rankName,
    secondaryName: (r) => `#${r.id ?? '—'}`,
    imageOf: (r) => r.logo_full_url || r.image_full_url || null,
    metricLabel: (r) => `Orders: ${numOrDash(r.order_count)}`,
    rowHref: (r) => (r.id != null ? `${routes.stores}/${r.id}/detail` : null),
  },
  popularStores: {
    title: 'Most Popular Stores',
    subtitle: 'Ranked by wishlist saves',
    emptyTitle: 'No stores available',
    emptyDescription: 'No popular stores for this selection yet.',
    viewAllTo: routes.stores,
    entityType: 'restaurant' as const,
    primaryName: (r) => rankName(r.store ?? r),
    secondaryName: (r) => (r.store_id != null ? `#${r.store_id}` : `#${r.id ?? '—'}`),
    imageOf: (r) => r.store?.logo_full_url || r.store?.image_full_url || r.logo_full_url || null,
    metricLabel: (r) => `Wishlist: ${numOrDash(r.count)}`,
    rowHref: (r) => {
      const id = r.store?.id ?? r.store_id ?? r.id;
      return id != null ? `${routes.stores}/${id}/detail` : null;
    },
  },
  topItems: {
    title: 'Top Selling Items',
    subtitle: 'Ranked by units sold',
    emptyTitle: 'No items available',
    emptyDescription: 'No items have sales for this selection yet.',
    viewAllTo: routes.items,
    entityType: 'food' as const,
    primaryName: rankName,
    secondaryName: (r) => `#${r.id ?? '—'}`,
    imageOf: (r) => r.image_full_url || null,
    metricLabel: (r) => `Sold: ${numOrDash(r.order_count)}`,
    rowHref: (r) => (r.id != null ? `${routes.items}/${r.id}` : null),
  },
  topRatedItems: {
    title: 'Most Rated Items',
    subtitle: 'Ranked by rating count',
    emptyTitle: 'No items available',
    emptyDescription: 'No items have ratings for this selection yet.',
    viewAllTo: routes.items,
    entityType: 'food' as const,
    primaryName: rankName,
    secondaryName: (r) =>
      r.avg_rating != null ? `★ ${Number(r.avg_rating).toFixed(1)}` : `#${r.id ?? '—'}`,
    imageOf: (r) => r.image_full_url || null,
    metricLabel: (r) => `Ratings: ${numOrDash(r.rating_count)}`,
    rowHref: (r) => (r.id != null ? `${routes.items}/${r.id}` : null),
  },
  topDeliverymen: {
    title: 'Top Deliveryman',
    subtitle: 'Ranked by assigned orders',
    emptyTitle: 'No deliveryman available',
    emptyDescription: 'No deliverymen have orders for this selection yet.',
    viewAllTo: routes.deliverymen,
    entityType: 'deliveryman' as const,
    primaryName: rankName,
    secondaryName: (r) => r.phone || `#${r.id ?? '—'}`,
    imageOf: (r) => r.image_full_url || null,
    metricLabel: (r) => `Orders: ${numOrDash(r.orders_count)}`,
    rowHref: (r) => (r.id != null ? `${routes.deliverymen}/${r.id}/detail` : null),
  },
  topCustomers: {
    title: 'Top Customers',
    subtitle: 'Ranked by order count',
    emptyTitle: 'No customer available',
    emptyDescription: 'No customers have orders for this selection yet.',
    viewAllTo: routes.customers,
    entityType: 'customer' as const,
    primaryName: rankName,
    secondaryName: (r) => r.phone || `#${r.id ?? '—'}`,
    imageOf: (r) => r.image_full_url || null,
    metricLabel: (r) => `Orders: ${numOrDash(r.order_count)}`,
    rowHref: (r) => (r.id != null ? `${routes.customers}/${r.id}` : null),
  },
};

function listOrEmpty(v: RankingRowBase[] | undefined): RankingRowBase[] {
  return Array.isArray(v) ? v : [];
}

function toCommissionPeriod(period: DashboardPeriod): CommissionPeriod {
  if (period === 'this_year' || period === 'this_month' || period === 'this_week') {
    return period;
  }
  return 'overall';
}

function toUserPeriod(period: DashboardPeriod): UserOverviewPeriod {
  if (period === 'this_year' || period === 'this_month' || period === 'this_week') {
    return period;
  }
  return 'overall';
}

export function DashboardPage() {
  const [zoneId, setZoneId] = useState<string>('all');
  const [period, setPeriod] = useState<DashboardPeriod>('this_year');
  const [salesPeriod, setSalesPeriod] = useState<CommissionPeriod>('this_year');
  const [userPeriod, setUserPeriod] = useState<UserOverviewPeriod>('this_year');
  const [salesModalOpen, setSalesModalOpen] = useState(false);
  const [entityModal, setEntityModal] = useState<EntityModalRef | null>(null);

  const filters = useMemo(
    () => ({
      zone_id: zoneId,
      statistics_type: period,
      commission_overview: salesPeriod,
      user_overview: userPeriod,
    }),
    [zoneId, period, salesPeriod, userPeriod],
  );

  const zonesQuery = useDashboardZones();
  const ordersQuery = useDashboardOrderStatistics(filters);
  const salesQuery = useDashboardSales(filters);
  // Rankings honor zone + module on the server (not period — documented backend semantic)
  const rankingsQuery = useDashboardRankings(zoneId);
  const rankings = rankingsQuery.data?.data;

  const stats = ordersQuery.data?.data.stats;
  const sales = salesQuery.data?.data;

  const grossSaleValues = normalizeSeries(sales?.grossSale);
  const commissionValues = normalizeSeries(sales?.commission);
  const deliveryCommissionValues = normalizeSeries(sales?.deliveryCommission);
  const labels = normalizeLabels(sales?.label);
  const grossSale = sumSeries(grossSaleValues);
  const commissionTotal = sumSeries(commissionValues);
  const deliveryCommissionTotal = sumSeries(deliveryCommissionValues);

  const salesSeries = grossSaleValues.map((value, i) => ({
    label: labels[i] ?? String(i + 1),
    value,
  }));

  const isFiltering =
    ordersQuery.isFetching ||
    salesQuery.isFetching ||
    (ordersQuery.isLoading && !stats) ||
    (salesQuery.isLoading && !sales);

  const onPeriodChange = (next: string) => {
    const nextPeriod = next as DashboardPeriod;
    setPeriod(nextPeriod);
    // Keep dependent sections filter-aware with the global period.
    setSalesPeriod(toCommissionPeriod(nextPeriod));
    setUserPeriod(toUserPeriod(nextPeriod));
  };

  return (
    <div className="cmd-dashboard">
      <div className="cmd-ambient" aria-hidden />

      <header className="cmd-header">
        <div className="cmd-header-main">
          <p className="cmd-eyebrow">
            <Sparkles size={14} aria-hidden />
            Business Command Center
          </p>
          <h1 className="dash-title">Dashboard</h1>
          <p className="dash-subtitle">
            Monitor orders, revenue and operational performance across your business.
          </p>
        </div>
        <div className="cmd-header-controls">
          <Field label="Zone">
            <Select
              value={zoneId}
              onChange={setZoneId}
              options={[
                { value: 'all', label: 'All Zones' },
                ...(zonesQuery.data ?? []).map((z) => ({
                  value: String(z.id),
                  label: z.name,
                })),
              ]}
            />
          </Field>
          <div className="cmd-period-field">
            <span className="form-label">Period</span>
            <PeriodTabs
              name="Dashboard period"
              value={period}
              onChange={onPeriodChange}
              options={PERIOD_OPTIONS}
            />
          </div>
          {isFiltering ? (
            <span className="cmd-refreshing" role="status">
              <RefreshCw size={13} aria-hidden />
              Updating
            </span>
          ) : null}
        </div>
      </header>

      {ordersQuery.error && !stats ? (
        <div className="card">
          <ErrorState
            description="Unable to load order statistics."
            onRetry={() => void ordersQuery.refetch()}
          />
        </div>
      ) : null}

      <BusinessSnapshot
        stats={stats}
        grossSale={grossSale}
        loading={ordersQuery.isLoading && !stats}
        onOpenSales={() => setSalesModalOpen(true)}
      />

      <OrderFlow stats={stats} loading={ordersQuery.isLoading && !stats} />

      <div className="cmd-revenue-grid">
        <RevenuePerformance
          grossSale={grossSale}
          series={salesSeries}
          loading={salesQuery.isLoading && !sales}
          error={!!salesQuery.error}
          onRetry={() => void salesQuery.refetch()}
          aside={
            <select
              className="form-control form-control-sm chart-select"
              value={salesPeriod}
              onChange={(e) => setSalesPeriod(e.target.value as CommissionPeriod)}
              aria-label="Sales period"
            >
              <option value="this_year">This year</option>
              <option value="this_month">This month</option>
              <option value="this_week">This week</option>
              <option value="overall">Overall</option>
            </select>
          }
        />
        <UserDistribution
          zoneId={zoneId}
          period={userPeriod}
          onPeriodChange={setUserPeriod}
        />
      </div>

      <NeedsAttention stats={stats} loading={ordersQuery.isLoading && !stats} />

      <section className="cmd-section" aria-label="Performance rankings">
        <header className="section-header">
          <div className="section-header-text">
            <h2 className="section-title">Performance Rankings</h2>
          </div>
        </header>
        <div className="dash-rank-grid">
          <RankingCard
            title="Top Selling Stores"
            subtitle="By order count"
            rows={listOrEmpty(rankings?.topRestaurants)}
            loading={rankingsQuery.isLoading}
            error={!!rankingsQuery.error}
            onRetry={() => void rankingsQuery.refetch()}
            config={RANK_WIDGETS.topStores}
            onOpenDetail={(type, id) => setEntityModal({ type, id })}
          />
          <RankingCard
            title="Most Popular Stores"
            subtitle="By wishlist saves"
            rows={listOrEmpty(rankings?.popularRestaurants)}
            loading={rankingsQuery.isLoading}
            error={!!rankingsQuery.error}
            onRetry={() => void rankingsQuery.refetch()}
            config={RANK_WIDGETS.popularStores}
            onOpenDetail={(type, id) => setEntityModal({ type, id })}
          />
          <RankingCard
            title="Top Selling Items"
            subtitle="By units sold"
            rows={listOrEmpty(rankings?.topSellingItems)}
            loading={rankingsQuery.isLoading}
            error={!!rankingsQuery.error}
            onRetry={() => void rankingsQuery.refetch()}
            config={RANK_WIDGETS.topItems}
            onOpenDetail={(type, id) => setEntityModal({ type, id })}
          />
          <RankingCard
            title="Most Rated Items"
            subtitle="By rating count"
            rows={listOrEmpty(rankings?.topRatedItems)}
            loading={rankingsQuery.isLoading}
            error={!!rankingsQuery.error}
            onRetry={() => void rankingsQuery.refetch()}
            config={RANK_WIDGETS.topRatedItems}
            onOpenDetail={(type, id) => setEntityModal({ type, id })}
          />
          <RankingCard
            title="Top Deliveryman"
            subtitle="By assigned orders"
            rows={listOrEmpty(rankings?.topDeliverymen)}
            loading={rankingsQuery.isLoading}
            error={!!rankingsQuery.error}
            onRetry={() => void rankingsQuery.refetch()}
            config={RANK_WIDGETS.topDeliverymen}
            onOpenDetail={(type, id) => setEntityModal({ type, id })}
          />
          <RankingCard
            title="Top Customers"
            subtitle="By order count"
            rows={listOrEmpty(rankings?.topCustomers)}
            loading={rankingsQuery.isLoading}
            error={!!rankingsQuery.error}
            onRetry={() => void rankingsQuery.refetch()}
            config={RANK_WIDGETS.topCustomers}
            onOpenDetail={(type, id) => setEntityModal({ type, id })}
          />
        </div>
      </section>

      <DashboardFooter />

      <SalesDetailsModal
        open={salesModalOpen}
        onClose={() => setSalesModalOpen(false)}
        grossSale={grossSale}
        commission={commissionTotal}
        deliveryCommission={deliveryCommissionTotal}
        series={salesSeries}
        period={salesPeriod}
        stats={stats}
        loading={salesQuery.isLoading && !sales}
      />
      <EntityDetailModal target={entityModal} onClose={() => setEntityModal(null)} />
    </div>
  );
}

export default DashboardPage;
